import { useState, useEffect } from 'react'
// import { useDarkMode } from '../../providers/DarkModeProvider'
import {
  MdPalette,
  MdNotifications,
  MdSecurity,
  MdAccessibility,
  MdLanguage,
  MdLogout,
  MdSave,
  MdRefresh,
  MdWarning,
  MdInfo,
} from 'react-icons/md'
import Button from '../../components/ui/Button'
import SettingsCard from '../../components/ui/SettingsCard'
import SettingsSelect from '../../components/ui/SettingsSelect'
import SettingsToggle from '../../components/ui/SettingsToggle'
import { useAuth } from '../../hooks/useAuth'
import {
  loadSettings,
  saveSettings,
  updateSetting,
  resetSettings,
  getTimezoneOptions,
  getLocaleOptions,
  getFontSizeOptions,
  validateSettings,
  DEFAULT_SETTINGS,
} from '../../utils/settings'
// import { useSelector } from 'react-redux'
// import { selectActiveUser } from '../../state/user.slice'
import './settings.styles.css'

function SettingsPage() {
  // const { isDarkMode, toggleDarkMode } = useDarkMode()
  const { logoutAsync } = useAuth()
  // const user = useSelector(selectActiveUser)
  const [settings, setSettings] = useState(DEFAULT_SETTINGS)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [showResetConfirm, setShowResetConfirm] = useState(false)
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState('')

  useEffect(() => {
    // Load settings
    const loadData = () => {
      try {
        const savedSettings = loadSettings()
        setSettings(savedSettings)
      } catch (error) {
        console.error('Error loading settings:', error)
        setMessage('Error loading settings')
        setMessageType('error')
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [])

  const handleSettingChange = (category, key, value) => {
    const updated = updateSetting(settings, category, key, value)
    setSettings(updated)

    // Show success message
    setMessage('Setting updated successfully')
    setMessageType('success')
    setTimeout(() => setMessage(''), 3000)
  }

  const handleSaveAll = async () => {
    setIsSaving(true)
    try {
      const errors = validateSettings(settings)
      if (errors.length > 0) {
        setMessage(`Validation errors: ${errors.join(', ')}`)
        setMessageType('error')
        return
      }

      const success = saveSettings(settings)
      if (success) {
        setMessage('Settings saved successfully')
        setMessageType('success')
      } else {
        setMessage('Error saving settings')
        setMessageType('error')
      }
    } catch (error) {
      console.error('Error saving settings:', error)
      setMessage('Error saving settings')
      setMessageType('error')
    } finally {
      setIsSaving(false)
      setTimeout(() => setMessage(''), 3000)
    }
  }

  const handleResetSettings = () => {
    const reset = resetSettings()
    setSettings(reset)
    setShowResetConfirm(false)
    setMessage('Settings reset to defaults')
    setMessageType('success')
    setTimeout(() => setMessage(''), 3000)
  }

  const handleLogout = async () => {
    try {
      await logoutAsync()
      // The logoutAsync function will handle the redirect
    } catch (error) {
      console.error('Error during logout:', error)
      setMessage('Error during logout')
      setMessageType('error')
      setTimeout(() => setMessage(''), 3000)
    }
  }

  // const getDarkModeLabel = (mode) => {
  //   switch (mode) {
  //     case 'auto': return 'System'
  //     case 'light': return 'Light'
  //     case 'dark': return 'Dark'
  //     default: return 'System'
  //   }
  // }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {/* <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center">
            <MdSettings className="text-primary-600 dark:text-primary-400 text-xl" />
          </div> */}
          <div className="text-left">
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Settings</h1>
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              Manage your account preferences and system settings
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            onClick={() => setShowResetConfirm(true)}
            className="flex items-center space-x-2"
          >
            <MdRefresh className="w-4 h-4" />
            <span>Reset</span>
          </Button>
          <Button
            onClick={handleSaveAll}
            loading={isSaving}
            className="flex items-center space-x-2"
          >
            <MdSave className="w-4 h-4" />
            <span>Save All</span>
          </Button>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div
          className={`rounded-lg p-4 ${
            messageType === 'success'
              ? 'bg-success-50 dark:bg-success-900/50 text-success-700 dark:text-success-200'
              : 'bg-error-50 dark:bg-error-900/50 text-error-700 dark:text-error-200'
          }`}
        >
          <div className="flex items-center space-x-2">
            {messageType === 'success' ? (
              <MdInfo className="w-5 h-5" />
            ) : (
              <MdWarning className="w-5 h-5" />
            )}
            <span>{message}</span>
          </div>
        </div>
      )}

      {/* Appearance Settings */}
      <SettingsCard
        title="Appearance"
        description="Customize the look and feel of the application"
        icon={<MdPalette className="w-5 h-5" />}
        className="settings-card-hover"
      >
        <div className="space-y-4">
          <SettingsSelect
            label="Dark Mode"
            description="Choose your preferred theme"
            value={settings.appearance.darkMode}
            onChange={(e) => handleSettingChange('appearance', 'darkMode', e.target.value)}
            options={[
              { value: 'auto', label: 'System' },
              { value: 'light', label: 'Light' },
              { value: 'dark', label: 'Dark' },
            ]}
          />

          <SettingsSelect
            label="Font Size"
            description="Adjust the text size throughout the application"
            value={settings.appearance.fontSize}
            onChange={(e) => handleSettingChange('appearance', 'fontSize', e.target.value)}
            options={getFontSizeOptions()}
          />

          <SettingsToggle
            label="Compact Mode"
            description="Reduce spacing for a more compact layout"
            checked={settings.appearance.compactMode}
            onChange={(value) => handleSettingChange('appearance', 'compactMode', value)}
            layout="horizontal"
          />
        </div>
      </SettingsCard>

      {/* Notification Settings */}
      <SettingsCard
        title="Notifications"
        description="Manage how you receive notifications"
        icon={<MdNotifications className="w-5 h-5" />}
        className="settings-card-hover"
      >
        <div className="space-y-6">
          <SettingsToggle
            label="Email Notifications"
            description="Receive important updates via email"
            checked={settings.notifications.emailNotifications}
            onChange={(value) => handleSettingChange('notifications', 'emailNotifications', value)}
            layout="horizontal"
          />

          <SettingsToggle
            label="Push Notifications"
            description="Receive real-time notifications in your browser"
            checked={settings.notifications.pushNotifications}
            onChange={(value) => handleSettingChange('notifications', 'pushNotifications', value)}
            layout="horizontal"
          />

          <SettingsToggle
            label="Sound Notifications"
            description="Play sounds for new notifications"
            checked={settings.notifications.soundEnabled}
            onChange={(value) => handleSettingChange('notifications', 'soundEnabled', value)}
            layout="horizontal"
          />

          <SettingsToggle
            label="Marketing Emails"
            description="Receive promotional and marketing content"
            checked={settings.notifications.marketingEmails}
            onChange={(value) => handleSettingChange('notifications', 'marketingEmails', value)}
            layout="horizontal"
          />
        </div>
      </SettingsCard>

      {/* Privacy Settings */}
      <SettingsCard
        title="Privacy & Data"
        description="Control how your data is collected and used"
        icon={<MdSecurity className="w-5 h-5" />}
        className="settings-card-hover"
      >
        <div className="space-y-6">
          <SettingsToggle
            label="Data Collection"
            description="Allow us to collect usage data to improve the service"
            checked={settings.privacy.dataCollection}
            onChange={(value) => handleSettingChange('privacy', 'dataCollection', value)}
            layout="horizontal"
          />

          <SettingsToggle
            label="Analytics"
            description="Help us improve by sharing anonymous usage statistics"
            checked={settings.privacy.analytics}
            onChange={(value) => handleSettingChange('privacy', 'analytics', value)}
            layout="horizontal"
          />

          <SettingsToggle
            label="Crash Reporting"
            description="Automatically send crash reports to help fix issues"
            checked={settings.privacy.crashReporting}
            onChange={(value) => handleSettingChange('privacy', 'crashReporting', value)}
            layout="horizontal"
          />
        </div>
      </SettingsCard>

      {/* Accessibility Settings */}
      <SettingsCard
        title="Accessibility"
        description="Customize the interface for better accessibility"
        icon={<MdAccessibility className="w-5 h-5" />}
        className="settings-card-hover"
      >
        <div className="space-y-6">
          <SettingsToggle
            label="High Contrast"
            description="Increase contrast for better visibility"
            checked={settings.accessibility.highContrast}
            onChange={(value) => handleSettingChange('accessibility', 'highContrast', value)}
            layout="horizontal"
          />

          <SettingsToggle
            label="Reduced Motion"
            description="Minimize animations and transitions"
            checked={settings.accessibility.reducedMotion}
            onChange={(value) => handleSettingChange('accessibility', 'reducedMotion', value)}
            layout="horizontal"
          />

          <SettingsToggle
            label="Screen Reader Support"
            description="Optimize interface for screen readers"
            checked={settings.accessibility.screenReader}
            onChange={(value) => handleSettingChange('accessibility', 'screenReader', value)}
            layout="horizontal"
          />
        </div>
      </SettingsCard>

      {/* Language & Region Settings */}
      <SettingsCard
        title="Language & Region"
        description="Set your preferred language and timezone"
        icon={<MdLanguage className="w-5 h-5" />}
        className="settings-card-hover"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SettingsSelect
            label="Language"
            description="Choose your preferred language"
            value={settings.language.locale}
            onChange={(e) => handleSettingChange('language', 'locale', e.target.value)}
            options={getLocaleOptions()}
          />

          <SettingsSelect
            label="Timezone"
            description="Set your local timezone"
            value={settings.language.timezone}
            onChange={(e) => handleSettingChange('language', 'timezone', e.target.value)}
            options={getTimezoneOptions()}
          />
        </div>
      </SettingsCard>

      {/* Account Actions */}
      <SettingsCard
        title="Account Actions"
        description="Manage your account and session"
        className="settings-card-hover"
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-neutral-50 dark:bg-neutral-800 rounded-lg">
            <div>
              <h4 className="font-medium text-neutral-900 dark:text-white">Sign Out</h4>
              <p className="text-sm text-neutral-600 dark:text-neutral-400">
                Sign out of your account on this device
              </p>
            </div>
            <Button
              variant="danger"
              onClick={() => setShowLogoutConfirm(true)}
              className="flex items-center space-x-2"
            >
              <MdLogout className="w-4 h-4" />
              <span>Sign Out</span>
            </Button>
          </div>
        </div>
      </SettingsCard>

      {/* Reset Confirmation Modal */}
      {showResetConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 modal-backdrop flex items-center justify-center z-50">
          <div className="bg-surface-light dark:bg-surface-dark rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-2">
              Reset Settings
            </h3>
            <p className="text-neutral-600 dark:text-neutral-400 mb-4">
              Are you sure you want to reset all settings to their default values? This action
              cannot be undone.
            </p>
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowResetConfirm(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button variant="danger" onClick={handleResetSettings} className="flex-1">
                Reset
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 modal-backdrop flex items-center justify-center z-50">
          <div className="bg-surface-light dark:bg-surface-dark rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-2">
              Sign Out
            </h3>
            <p className="text-neutral-600 dark:text-neutral-400 mb-4">
              Are you sure you want to sign out? You'll need to sign in again to access your
              account.
            </p>
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowLogoutConfirm(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button variant="danger" onClick={handleLogout} className="flex-1">
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SettingsPage
